'use client';
import { getVariantFromSimpleControls } from '@/utils/animation';
import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import makeId from '@/utils/makeId';
import { normalizeSvgEmbedMarkup } from '@/utils/normalizeSvgEmbed';
import { parseImagePosition } from '@/utils/parseImagePosition';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { CompareSlider } from './atoms/CompareSlider';
import IconValue from './atoms/IconValue';
import { MarkDown } from './atoms/MarkDown';
import VideoEmbed from './atoms/VideoEmbed';

const isEmbedCode = (val) =>
    typeof val === 'string' &&
    /<\s*(iframe|script|video|svg|div|section|figure|blockquote)[\s>]/i.test(
        val
    );

function normalizeMediaUrl(input) {
    if (!input) return '';
    if (typeof input === 'string') return input.trim();
    if (typeof input === 'object') {
        const candidate =
            input.Media ||
            input.media ||
            input.url ||
            input.src ||
            input.value ||
            '';
        return typeof candidate === 'string' ? candidate.trim() : '';
    }
    return '';
}

function isSelectedMedia(input) {
    const mediaUrl = normalizeMediaUrl(input);
    return Boolean(mediaUrl && mediaUrl !== '/images/placeholder.svg');
}

const SERVICE_TABS_NAV_MEDIA_LAYOUT = {
    slim: { top: 96, shift: -40 },
    compact: { top: 108, shift: -28 },
    standard: { top: 132, shift: -4 },
    large: { top: 156, shift: 20 },
};

function normalizeServiceTabsNavHeight(value) {
    const raw = String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[_\s]+/g, '-');
    if (['slim', 'tight', 'thin', 'minimal'].includes(raw)) return 'slim';
    if (['large', 'taller', 'extra-tall', 'x-tall'].includes(raw)) {
        return 'large';
    }
    if (raw === 'standard' || raw === 'tall') return 'standard';
    return 'compact';
}

function readServiceTabsNavHeightMode() {
    if (typeof document === 'undefined') return 'compact';
    const nav = document.querySelector('[data-component="Nav"]');
    return normalizeServiceTabsNavHeight(
        nav?.dataset?.navHeight ||
            document.documentElement?.dataset?.navHeightMode ||
            'compact'
    );
}

function normalizeAnimation(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'slide',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'slide';
}

function MotionMaybe({ enable, k, motionProps, className, style, children }) {
    if (enable) {
        return (
            <motion.div
                key={`${k}-on`}
                className={className}
                style={style}
                {...motionProps}
            >
                {children}
            </motion.div>
        );
    }
    return (
        <div
            key={`${k}-off`}
            className={className}
            style={{ ...style, opacity: '', transform: '' }}
        >
            {children}
        </div>
    );
}

function valOf(x, fallback = '') {
    if (x == null) return fallback;
    if (typeof x === 'object' && 'value' in x) return x.value ?? fallback;
    return x;
}

function hasMeaningfulText(value) {
    const raw = valOf(value, '');
    if (raw == null) return false;
    const text = String(raw)
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;|&#160;/gi, ' ')
        .replace(/\u00a0/g, ' ')
        .trim();
    return text.length > 0;
}

function featureHasContent(feature) {
    if (!feature || typeof feature !== 'object') return false;
    const icon = String(valOf(feature.Icon, '')).trim();
    return (
        icon.length > 0 ||
        hasMeaningfulText(feature.Title) ||
        hasMeaningfulText(feature.Description)
    );
}

export default function Services(props) {
    const {
        Header,
        Body,
        Services,
        Alignment = 'left',
        VerticalSpacing = 'medium',
        ColorScheme = 'white',
        ImagePosition = 'left',
        ImageVariant = 'cover',
        StickyMedia = false,
        Animation = 'fade',
        AnimationPreset,
        Duration = 0.6,
        AnimationGroup = 'features',
        EditorPreview = false,
    } = props;

    const [activeTab, setActiveTab] = useState(0);

    const limitedServices = Services.slice(0, 15);

    let textAlign, itemAlign, itemPadding, tabNavAlign;
    switch (Alignment) {
        case 'center':
            textAlign = 'text-center';
            itemAlign = 'items-center';
            tabNavAlign = 'mx-auto justify-center';
            itemPadding = 'mx-1 lg:mx-2';
            break;
        default: // left
            textAlign = 'text-left';
            itemAlign = 'items-start';
            tabNavAlign = 'justify-start';
            itemPadding = 'mr-3 lg:mr-4';
            break;
    }

    const colorSchemeToUse = ColorScheme.startsWith('box')
        ? 'white'
        : ColorScheme.trim();

    const { backgroundColor, textColor, headerColor, backgroundStyle } =
        getColorScheme(colorSchemeToUse, props.GradientDirection);

    const borderColor = textColor.replace('text-', 'border-');
    const smallTabColor = textColor;

    useEffect(() => {
        function setActiveTabFromHash() {
            const hash = window.location.hash.substring(1);
            const index = Services.findIndex(({ Anchor }) => Anchor === hash);

            if (index > -1 && index !== activeTab) {
                setActiveTab(index);
            }
        }

        setActiveTabFromHash();

        window.addEventListener('hashchange', setActiveTabFromHash);

        return () => {
            window.removeEventListener('hashchange', setActiveTabFromHash);
        };
    }, [Services, activeTab]);

    function handleTabClick(index) {
        const hash = Services[index].Anchor;
        window.location.hash = hash;
    }
    const vSpace = getVerticalSpacing(VerticalSpacing);

    return (
        <section
            className={`editable-component relative ${backgroundColor} ${vSpace} px-6 lg:px-8`}
            style={backgroundStyle}
        >
            <div data-json="services">
                <div className="relative z-40 mx-auto max-w-2xl lg:max-w-7xl">
                    {/*Section Intro*/}
                    {(Header || Body) && (
                        <div
                            className={`mb-10 ${textAlign} mx-auto flex w-full flex-col md:text-4xl lg:mb-20 lg:text-5xl ${itemAlign} lg:mx-0`}
                        >
                            <MarkDown
                                variant="h2"
                                className={`max-w-2xl font-display text-3xl tracking-tight ${headerColor} md:mx-auto md:text-4xl lg:mx-0 lg:max-w-3xl`}
                            >
                                {Header}
                            </MarkDown>
                            <MarkDown
                                className={`mt-6 max-w-2xl text-xl font-text leading-8 ${textColor} md:mx-auto lg:mx-0 lg:max-w-3xl`}
                            >
                                {Body}
                            </MarkDown>
                        </div>
                    )}
                    {/* Tab Navigation */}
                    <div
                        className={`mb-4 flex flex-wrap lg:mb-8 ${tabNavAlign}`}
                    >
                        {limitedServices.map((service, index) => (
                            <button
                                key={index}
                                onClick={() => handleTabClick(index)}
                                className={`mb-3 lg:mb-4 ${itemPadding} rounded-[var(--shape-radius)] px-5 py-3 text-sm font-semibold transition-all duration-300 ease-in-out lg:text-base ${
                                    index === activeTab
                                        ? `border bg-[rgba(128,128,128,0.2)] ${smallTabColor} ${borderColor}`
                                        : `border bg-transparent ${smallTabColor} border ${borderColor}`
                                } mb-3 lg:mb-4  ${itemPadding} rounded-[var(--shape-radius)] px-5 py-3 text-sm font-semibold transition-colors duration-300 ease-in-out lg:text-base`}
                            >
                                <MarkDown
                                    className="leading-5"
                                    components={{
                                        p: ({ children, ...rest }) => (
                                            <span
                                                {...rest}
                                                className="inline-block"
                                            >
                                                {children}
                                            </span>
                                        ),
                                    }}
                                >
                                    {service.TabName}
                                </MarkDown>
                            </button>
                        ))}

                        {/* Tab Anchors */}
                        {limitedServices.map(({ Anchor }) => (
                            <div
                                key={Anchor}
                                className="absolute -mt-8 lg:-mt-12"
                                id={makeId(Anchor)}
                            ></div>
                        ))}
                    </div>
                </div>

                {/* Tab Content */}
                <div>
                    {limitedServices.map((service, index) => (
                        <div
                            key={index}
                            className={`${
                                index === activeTab ? 'block' : 'hidden'
                            }`}
                        >
                            <ServiceTab
                                {...service}
                                ColorScheme={ColorScheme}
                                BeforeImage={service.BeforeImage}
                                AfterImage={service.AfterImage}
                                Medias={service.Medias}
                                ImagePosition={ImagePosition}
                                ImageVariant={ImageVariant}
                                StickyMedia={StickyMedia}
                                Animation={Animation}
                                AnimationPreset={AnimationPreset}
                                Duration={Duration}
                                AnimationGroup={AnimationGroup}
                                EditorPreview={EditorPreview}
                            />
                        </div>
                    ))}
                </div>
                {/* Warning if more than 15 services */}
                {Services.length > 15 && (
                    <div className="mt-4 text-center text-lg text-red-600">
                        A maximum of 15 items can be displayed.
                    </div>
                )}
            </div>
        </section>
    );
}

function ServiceTab(props) {
    const {
        Tagline,
        Header,
        Body,
        BeforeImage,
        AfterImage,
        BeforeAltText,
        AfterAltText,
        Features = [],

        OutlineButtonText: OutlineButtonTextProp,
        OutlineButtonLink: OutlineButtonLinkProp,
        OutlineButtonOpenInNewTab: OutlineButtonOpenInNewTabProp,
        ThirdButtonText, // legacy fallback
        ThirdButtonLink, // legacy fallback
        ThirdButtonOpenInNewTab, // legacy fallback
        ColorScheme,
        ImagePosition,
        ImageVariant,
        StickyMedia = false,
        Medias = [],
        Alignment = 'left',
        Animation = 'fade',
        AnimationPreset,
        Duration = 0.6,
        AnimationGroup = 'features',
        EditorPreview = false,
    } = props;

    const visibleFeatures = Array.isArray(Features)
        ? Features.map((feature, originalIndex) => ({
              feature,
              originalIndex,
          })).filter(({ feature }) => featureHasContent(feature))
        : [];

    const { textColor, secondaryButton, thirdButton, taglineColor } =
        getColorScheme(ColorScheme);
    const OutlineButtonText = OutlineButtonTextProp ?? ThirdButtonText ?? '';
    const OutlineButtonLink = OutlineButtonLinkProp ?? ThirdButtonLink ?? '';
    const OutlineButtonOpenInNewTab =
        OutlineButtonTextProp != null
            ? OutlineButtonOpenInNewTabProp
            : ThirdButtonOpenInNewTab;

    /* -------------------------- Animation -------------------------- */
    const preset = normalizeAnimation(Animation ?? AnimationPreset ?? 'slide');

    const [prefersReduced, setPrefersReduced] = useState(false);
    useEffect(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
            const update = (e) => setPrefersReduced(e.matches);
            setPrefersReduced(mq.matches);
            mq.addEventListener?.('change', update);
            return () => mq.removeEventListener?.('change', update);
        }
    }, []);

    const isStagger = preset === 'stagger';
    const viewport = { once: !EditorPreview, amount: 0.5 };

    const rawGroup = String(AnimationGroup || 'content')
        .toLowerCase()
        .trim();
    const allowed = new Set([
        'features',
        'feature',
        'content',
        'ctas',
        'medias',
        'media',
        'image',
        'images',
        'background',
        'bg',
    ]);
    const group = allowed.has(rawGroup)
        ? rawGroup === 'feature'
            ? 'features'
            : ['media', 'image', 'images', 'background', 'bg'].includes(
                  rawGroup
              )
            ? 'medias'
            : rawGroup
        : 'content';

    const animateMedia = group === 'medias';
    const animateTextBlocks = group === 'content';
    const animateKeyFeatures = group === 'content' || group === 'features';
    const animateCTAs = group === 'content' || group === 'ctas';

    const baseKey = `${group}-${preset}-${Duration}-${
        EditorPreview ? 'preview' : 'prod'
    }`;

    const containerMotionProps = useMemo(() => {
        if (prefersReduced) return {};
        if (isStagger) {
            const variants = getVariantFromSimpleControls('stagger', Duration);
            if (EditorPreview)
                return { variants, initial: 'hidden', animate: 'show' };
            return {
                variants,
                initial: 'hidden',
                whileInView: 'show',
                viewport,
            };
        }
        return {};
    }, [prefersReduced, isStagger, Duration, EditorPreview]);

    const childMotionProps = useMemo(() => {
        if (prefersReduced) return {};
        if (isStagger) {
            const childVariants = {
                hidden: { opacity: 0, y: 16 },
                show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: Math.max(0.4, Duration - 0.2) },
                },
            };
            return { variants: childVariants };
        }
        if (preset === 'none') return {};
        const v = getVariantFromSimpleControls(preset, Duration);
        const hasOffscreen = v && 'offscreen' in v;
        const initialState = hasOffscreen ? 'offscreen' : 'hidden';
        const targetState = hasOffscreen ? 'onscreen' : 'show';
        if (EditorPreview)
            return { variants: v, initial: initialState, animate: targetState };
        return {
            variants: v,
            initial: initialState,
            whileInView: targetState,
            viewport,
        };
    }, [prefersReduced, isStagger, preset, Duration, EditorPreview]);
    const selectedMedias = useMemo(
        () =>
            Array.isArray(Medias)
                ? Medias.filter((media) => isSelectedMedia(media?.Media))
                : [],
        [Medias]
    );
    const hasBeforeAfter =
        isSelectedMedia(BeforeImage) && isSelectedMedia(AfterImage);
    const hasMediaContent = selectedMedias.length > 0 || hasBeforeAfter;
    const ctaButtonAlign =
        !hasMediaContent || Alignment === 'center'
            ? 'justify-center'
            : 'justify-start';

    const { hasAny: hasCTAs, element: CTAs } = getCTA(
        {
            ...props,
            OutlineButtonText,
            OutlineButtonLink,
            OutlineButtonOpenInNewTab,
        },
        {
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign: ctaButtonAlign,
            wrapSpacing: 'mt-10 lg:mt-16',
            enableAnim: animateCTAs && preset !== 'none' && !prefersReduced,
            motionProps: childMotionProps,
        }
    );

    const getImageVariantClasses = (variant) => {
        const isSquare = variant.includes('square');
        const isContain = variant.includes('contain');
        const hasShadow = !variant.includes('no-shadow');
        const textVerticalCenterClass = variant.includes('vertical-center')
            ? 'flex-col justify-center lg:my-auto'
            : '';

        const shape = isSquare
            ? 'rounded-[var(--shape-radius)]'
            : 'rounded-[min(1.5rem,var(--shape-radius))]';

        const shadow = hasShadow ? 'shadow-xl' : '';

        let imageClass = 'object-cover w-full aspect-[16/9] overflow-hidden';
        let sliderClass = 'w-full aspect-[16/9]';
        let sliderStyle;
        if (isContain) {
            imageClass = 'object-contain w-full';
            sliderClass = 'w-full';
        } else if (variant.includes('portrait')) {
            imageClass = 'object-cover aspect-[9/16] w-full mx-auto';
            sliderClass = 'w-full aspect-[9/16]';
            sliderStyle = { aspectRatio: '9 / 16' };
        } else if (isSquare) {
            imageClass = 'object-cover h-full w-full';
            sliderClass = 'relative w-full overflow-hidden';
            sliderStyle = { aspectRatio: '1 / 1' };
        }

        return {
            shape,
            shadow,
            imageClass,
            sliderClass,
            sliderStyle,
            textVerticalCenterClass,
        };
    };

    const {
        shape,
        shadow,
        imageClass,
        sliderClass,
        sliderStyle,
        textVerticalCenterClass,
    } = getImageVariantClasses(ImageVariant);

    const baseTextAlignClass =
        Alignment === 'center'
            ? 'text-center'
            : Alignment === 'right'
            ? 'text-right'
            : 'text-left';
    const textAlignClass = hasMediaContent ? baseTextAlignClass : 'text-center';
    const textWidthClass = hasMediaContent
        ? 'max-w-2xl lg:max-w-lg'
        : 'max-w-3xl';

    const mainGridItemsAlignment = ImageVariant.includes('vertical-center')
        ? 'lg:items-center'
        : 'lg:items-start';

    let mediaGridColsClass = '';
    if (ImageVariant.includes('portrait') && selectedMedias.length > 0) {
        mediaGridColsClass =
            selectedMedias.length === 1 ? 'grid-cols-1' : 'grid-cols-2';
    } else if (hasBeforeAfter) {
        mediaGridColsClass =
            selectedMedias.length === 1 ? 'grid-cols-1' : 'grid-cols-2';
    } else if (selectedMedias.length === 1) {
        mediaGridColsClass = 'grid-cols-1';
    } else if (selectedMedias.length >= 2) {
        mediaGridColsClass =
            ImageVariant === 'cover' ||
            ImageVariant.includes('landscape') ||
            ImageVariant.includes('square') ||
            ImageVariant.includes('cover-vertical-center')
                ? 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-1'
                : 'grid-cols-2 lg:grid-cols-1';
    }

    let imagePositionClass;
    switch (ImagePosition) {
        case 'right':
            imagePositionClass = 'lg:order-last';
            break;
        default: // left
            imagePositionClass = 'lg:order-first';
            break;
    }
    const gridColsClass = hasMediaContent ? 'lg:grid-cols-2' : 'lg:grid-cols-1';
    const stickyMediaEnabled = String(valOf(StickyMedia, false))
        .trim()
        .toLowerCase();
    const shouldStickMedia =
        hasMediaContent &&
        (stickyMediaEnabled === 'true' || stickyMediaEnabled === '1') &&
        ImagePosition !== 'center';
    const [serviceNavHeightMode, setServiceNavHeightMode] = useState('compact');

    useEffect(() => {
        if (!shouldStickMedia || typeof window === 'undefined') return;

        const updateNavHeightMode = () => {
            const nextMode = readServiceTabsNavHeightMode();
            setServiceNavHeightMode((prev) =>
                prev === nextMode ? prev : nextMode
            );
        };

        updateNavHeightMode();
        window.addEventListener('resize', updateNavHeightMode);
        return () => window.removeEventListener('resize', updateNavHeightMode);
    }, [shouldStickMedia]);

    const serviceNavMediaLayout =
        SERVICE_TABS_NAV_MEDIA_LAYOUT[serviceNavHeightMode] ||
        SERVICE_TABS_NAV_MEDIA_LAYOUT.compact;
    const stickyMediaClass = shouldStickMedia
        ? 'lg:sticky lg:self-start lg:top-[var(--service-tabs-sticky-media-top)] lg:mt-[var(--service-tabs-sticky-media-shift)]'
        : '';
    const stickyMediaStyle = shouldStickMedia
        ? {
              '--service-tabs-sticky-media-top': `${serviceNavMediaLayout.top}px`,
              '--service-tabs-sticky-media-shift': `${serviceNavMediaLayout.shift}px`,
          }
        : undefined;

    return (
        <div className="editable-component relative z-40 mx-auto max-w-7xl">
            {/* Feature Grid */}
            <div
                className={`grid grid-cols-1 gap-12 ${gridColsClass} ${mainGridItemsAlignment}`}
            >
                {hasMediaContent && (
                    <MotionMaybe
                        enable={
                            animateMedia && preset !== 'none' && !prefersReduced
                        }
                        k={`${baseKey}-media`}
                        motionProps={childMotionProps}
                        className={`mx-auto w-full ${imagePositionClass} relative flex flex-col gap-y-6 ${stickyMediaClass}`}
                        style={stickyMediaStyle}
                    >
                        {/* Before/After Slider */}
                        {hasBeforeAfter && (
                            <div
                                className={`w-full overflow-hidden ${sliderClass} ${shape} ${shadow}`}
                                style={sliderStyle}
                            >
                                <CompareSlider
                                    beforeSrc={BeforeImage}
                                    afterSrc={AfterImage}
                                    beforeAlt={BeforeAltText}
                                    afterAlt={AfterAltText}
                                    className="h-full w-full"
                                    imageClass={imageClass}
                                />
                            </div>
                        )}

                        {/* Media Grid */}
                        {selectedMedias.length > 0 && (
                            <div
                                className={`grid w-full gap-6 ${mediaGridColsClass}`}
                            >
                                {selectedMedias
                                    .slice(0, 2)
                                    .map((media, index) => {
                                        const isEmbed = isEmbedCode(
                                            media.Media
                                        );
                                        const isImage =
                                            /\.(jpg|jpeg|png|svg|webp|gif)$/i.test(
                                                media.Media
                                            );
                                        const { x: mediaPosX, y: mediaPosY } =
                                            parseImagePosition(
                                                media.MediaPosition
                                            );
                                        const mediaObjectPosition = `${mediaPosX}% ${mediaPosY}%`;

                                        let aspectRatioPadding;
                                        if (ImageVariant.includes('portrait')) {
                                            aspectRatioPadding = '177.78%';
                                        } else if (
                                            ImageVariant.includes('square')
                                        ) {
                                            aspectRatioPadding = '100%';
                                        } else if (
                                            ImageVariant.includes('cover')
                                        ) {
                                            aspectRatioPadding = '75%';
                                        } else if (
                                            ImageVariant.includes('landscape')
                                        ) {
                                            aspectRatioPadding = '56.25%';
                                        } else {
                                            aspectRatioPadding = '56.25%';
                                        }

                                        if (isEmbed) {
                                            const embedMarkup =
                                                normalizeSvgEmbedMarkup(
                                                    media.Media,
                                                    {
                                                        x: mediaPosX,
                                                        y: mediaPosY,
                                                        mode: ImageVariant.includes(
                                                            'contain'
                                                        )
                                                            ? 'contain'
                                                            : 'cover',
                                                    }
                                                );
                                            return (
                                                <div
                                                    key={index}
                                                    className="relative mx-auto h-full w-full"
                                                >
                                                    <div
                                                        className={`relative w-full overflow-hidden ${shape} ${shadow}`}
                                                        style={{
                                                            paddingTop:
                                                                aspectRatioPadding,
                                                        }}
                                                    >
                                                        <div
                                                            className="absolute inset-0 [&>*]:h-full [&>*]:max-h-none [&>*]:w-full [&>*]:max-w-none [&>svg]:block"
                                                            dangerouslySetInnerHTML={{
                                                                __html: embedMarkup,
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        }

                                        if (isImage) {
                                            return (
                                                <div
                                                    key={index}
                                                    className="relative mx-auto h-full w-full"
                                                >
                                                    {ImageVariant.includes(
                                                        'square'
                                                    ) ? (
                                                        <div
                                                            className={`aspect-h-1 aspect-w-1 w-full overflow-hidden ${shape} ${shadow}`}
                                                        >
                                                            <img
                                                                className="h-full w-full object-cover"
                                                                src={
                                                                    media.Media
                                                                }
                                                                alt={
                                                                    media.AltText ||
                                                                    ''
                                                                }
                                                                loading="lazy"
                                                                style={{
                                                                    objectPosition:
                                                                        mediaObjectPosition,
                                                                }}
                                                            />
                                                        </div>
                                                    ) : (ImageVariant.includes(
                                                          'cover'
                                                      ) ||
                                                          ImageVariant.includes(
                                                              'portrait'
                                                          ) ||
                                                          ImageVariant.includes(
                                                              'landscape'
                                                          )) &&
                                                      !ImageVariant.includes(
                                                          'contain'
                                                      ) ? (
                                                        (() => {
                                                            const isSinglePortraitNonContain =
                                                                ImageVariant.includes(
                                                                    'portrait'
                                                                ) &&
                                                                selectedMedias.length ===
                                                                    1;
                                                            const containerClasses = `relative ${
                                                                isSinglePortraitNonContain
                                                                    ? 'mx-auto w-full'
                                                                    : 'w-full'
                                                            } overflow-hidden ${shape} ${shadow}`;
                                                            return (
                                                                <div
                                                                    className={
                                                                        containerClasses
                                                                    }
                                                                    style={{
                                                                        paddingTop:
                                                                            aspectRatioPadding,
                                                                    }}
                                                                >
                                                                    <img
                                                                        src={
                                                                            media.Media
                                                                        }
                                                                        alt={
                                                                            media.AltText ||
                                                                            ''
                                                                        }
                                                                        loading="lazy"
                                                                        className="absolute left-0 top-0 h-full w-full object-cover"
                                                                        style={{
                                                                            objectPosition:
                                                                                mediaObjectPosition,
                                                                        }}
                                                                    />
                                                                </div>
                                                            );
                                                        })()
                                                    ) : (
                                                        <img
                                                            className={`${imageClass} object-center ${shape} ${shadow}`}
                                                            width="1000"
                                                            height="1000"
                                                            src={media.Media}
                                                            alt={
                                                                media.AltText ||
                                                                ''
                                                            }
                                                            loading="lazy"
                                                            style={{
                                                                objectPosition:
                                                                    mediaObjectPosition,
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        }

                                        const videoShapeClass =
                                            ImageVariant.includes('square')
                                                ? 'rounded-[var(--shape-radius)]'
                                                : 'rounded-[min(1.5rem,var(--shape-radius))]';
                                        const wrapperClasses = `relative mx-auto w-full overflow-hidden ${videoShapeClass} ${shadow}`;

                                        return (
                                            <div
                                                key={index}
                                                className={wrapperClasses}
                                                style={{
                                                    paddingTop:
                                                        aspectRatioPadding,
                                                }}
                                            >
                                                <VideoEmbed
                                                    url={media.Media}
                                                    controls
                                                    className="absolute left-0 top-0 h-full w-full"
                                                    width="100%"
                                                    height="100%"
                                                />
                                            </div>
                                        );
                                    })}
                            </div>
                        )}
                    </MotionMaybe>
                )}

                {/* Text Content */}
                <div
                    className={`mx-auto flex w-full ${textVerticalCenterClass} ${textAlignClass} ${
                        !hasMediaContent ? 'justify-center' : ''
                    }`}
                >
                    <div className={textWidthClass}>
                        <MotionMaybe
                            enable={
                                animateTextBlocks &&
                                preset !== 'none' &&
                                !prefersReduced
                            }
                            k={`${baseKey}-tagline`}
                            motionProps={childMotionProps}
                        >
                            <MarkDown
                                className={`font-display text-base font-bold uppercase leading-7 tracking-wide ${taglineColor}`}
                                components={{
                                    p: ({ children, ...rest }) => (
                                        <p
                                            {...rest}
                                            className={`mb-0 font-display text-base font-bold uppercase leading-7 tracking-wide ${taglineColor}`}
                                        >
                                            {children}
                                        </p>
                                    ),
                                }}
                            >
                                {Tagline}
                            </MarkDown>
                        </MotionMaybe>
                        <MotionMaybe
                            enable={
                                animateTextBlocks &&
                                preset !== 'none' &&
                                !prefersReduced
                            }
                            k={`${baseKey}-header`}
                            motionProps={childMotionProps}
                        >
                            <MarkDown
                                variant="h2"
                                className={`mt-2 font-display text-2xl tracking-tight ${textColor} md:text-3xl lg:text-4xl`}
                            >
                                {Header}
                            </MarkDown>
                        </MotionMaybe>
                        <MotionMaybe
                            enable={
                                animateTextBlocks &&
                                preset !== 'none' &&
                                !prefersReduced
                            }
                            k={`${baseKey}-body`}
                            motionProps={childMotionProps}
                        >
                            <MarkDown
                                className={`mt-6 text-lg font-text leading-7 ${textColor}`}
                            >
                                {Body}
                            </MarkDown>
                        </MotionMaybe>
                        {visibleFeatures.length > 0 && (
                            <MotionMaybe
                                enable={
                                    animateKeyFeatures &&
                                    isStagger &&
                                    !prefersReduced &&
                                    preset !== 'none'
                                }
                                k={`${baseKey}-features-container`}
                                motionProps={containerMotionProps}
                                className="mt-10"
                            >
                                <div
                                    className={`max-w-xl space-y-5 text-base leading-7 ${textColor} lg:max-w-none`}
                                >
                                    {visibleFeatures.map(
                                        ({ feature, originalIndex }) =>
                                            isStagger &&
                                            animateKeyFeatures &&
                                            !prefersReduced &&
                                            preset !== 'none' ? (
                                                <motion.div
                                                    key={`${
                                                        feature?.Header ??
                                                        'feature'
                                                    }-${originalIndex}-${baseKey}`}
                                                    variants={{
                                                        hidden: {
                                                            opacity: 0,
                                                            y: 16,
                                                        },
                                                        show: {
                                                            opacity: 1,
                                                            y: 0,
                                                            transition: {
                                                                duration:
                                                                    Math.max(
                                                                        0.4,
                                                                        Duration -
                                                                            0.2
                                                                    ),
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Feature
                                                        {...feature}
                                                        ColorScheme={
                                                            ColorScheme
                                                        }
                                                    />
                                                </motion.div>
                                            ) : (
                                                <MotionMaybe
                                                    key={`${
                                                        feature?.Header ??
                                                        'feature'
                                                    }-${originalIndex}-${baseKey}`}
                                                    enable={
                                                        animateKeyFeatures &&
                                                        !isStagger &&
                                                        preset !== 'none' &&
                                                        !prefersReduced
                                                    }
                                                    k={`${baseKey}-feature-${originalIndex}`}
                                                    motionProps={
                                                        childMotionProps
                                                    }
                                                >
                                                    <Feature
                                                        {...feature}
                                                        ColorScheme={
                                                            ColorScheme
                                                        }
                                                    />
                                                </MotionMaybe>
                                            )
                                    )}
                                </div>
                            </MotionMaybe>
                        )}
                        {hasCTAs && CTAs}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Feature(props) {
    const { Icon, Title, Description, ColorScheme } = props;
    const { textColor, iconColor } = getColorScheme(ColorScheme);

    return (
        <div className="relative">
            <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-start justify-center">
                    <IconValue
                        value={Icon}
                        className={`text-2xl leading-none ${iconColor}`}
                    />
                </div>
                <div className="min-w-0 flex-1">
                    <div className={`font-bold ${textColor}`}>
                        <MarkDown
                            className="font-display"
                            components={{
                                p: ({ children, ...rest }) => (
                                    <span {...rest} className="font-display">
                                        {children}
                                    </span>
                                ),
                            }}
                        >
                            {Title}
                        </MarkDown>
                    </div>
                    <div>
                        <MarkDown>{Description}</MarkDown>
                    </div>
                </div>
            </div>
        </div>
    );
}
